import React, { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Download } from "lucide-react";

export default function ParametresTab() {
  const [s, setS] = useState(null);
  const [emails, setEmails] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);

  const save = async () => {
    const payload = {
      min_order_default: parseFloat(s.min_order_default) || 0,
      delivery_zones: s.delivery_zones.map((z) => ({ postal_code: z.postal_code, min_order: parseFloat(z.min_order) || 0, delivery_fee: parseFloat(z.delivery_fee) || 0 })),
      timezone_offset: parseInt(s.timezone_offset) || 0,
      printer_ip: s.printer_ip || "", printer_mac: s.printer_mac || "", phone: s.phone, restaurant_name: s.restaurant_name, address: s.address,
    };
    await api.put("/settings", payload);
    toast.success("Paramètres enregistrés");
  };

  const addZone = () => setS({ ...s, delivery_zones: [...s.delivery_zones, { postal_code: "", min_order: s.min_order_default, delivery_fee: 5 }] });
  const updZone = (i, patch) => setS({ ...s, delivery_zones: s.delivery_zones.map((z, j) => (j === i ? { ...z, ...patch } : z)) });
  const delZone = (i) => setS({ ...s, delivery_zones: s.delivery_zones.filter((_, j) => j !== i) });

  const exportEmails = async () => {
    const { data } = await api.get("/settings/marketing-emails");
    setEmails(data.emails);
    const blob = new Blob([data.emails.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "emails_marketing.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success(`${data.count} emails exportés`);
  };

  if (!s) return null;

  return (
    <div className="max-w-3xl">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display text-2xl font-bold">Paramètres</h2>
        <Button onClick={save} className="rounded-none bg-brand-red hover:bg-brand-red/90" data-testid="save-settings-btn">Enregistrer</Button>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Commande minimum par défaut (CHF)</Label><Input type="number" value={s.min_order_default} onChange={(e) => setS({ ...s, min_order_default: e.target.value })} className="rounded-none bg-neutral-800 border-white/20" data-testid="min-order-input" /></div>
          <div><Label>Décalage timezone (h)</Label><Input type="number" value={s.timezone_offset} onChange={(e) => setS({ ...s, timezone_offset: e.target.value })} className="rounded-none bg-neutral-800 border-white/20" /></div>
          <div><Label>IP imprimante</Label><Input value={s.printer_ip} onChange={(e) => setS({ ...s, printer_ip: e.target.value })} className="rounded-none bg-neutral-800 border-white/20" data-testid="printer-ip-input" /></div>
          <div><Label>Adresse MAC imprimante</Label><Input value={s.printer_mac || ""} onChange={(e) => setS({ ...s, printer_mac: e.target.value })} placeholder="00:11:62:2e:da:b8" className="rounded-none bg-neutral-800 border-white/20" data-testid="printer-mac-input" /></div>
          <div><Label>Téléphone</Label><Input value={s.phone} onChange={(e) => setS({ ...s, phone: e.target.value })} className="rounded-none bg-neutral-800 border-white/20" /></div>
        </div>

        <div className="border border-brand-amber/40 bg-brand-amber/5 p-3">
          <Label>Impression auto (Star CloudPRNT)</Label>
          <p className="text-xs text-white/60 mt-1">Dans la config de l'imprimante Star, réglez le « Server URL » sur :</p>
          <code className="block mt-1 text-xs bg-neutral-950 border border-white/20 p-2 break-all text-brand-amber" data-testid="cloudprnt-url">{`${window.location.origin}/api/cloudprnt/poll`}</code>
          <p className="text-[11px] text-white/40 mt-1">Chaque nouvelle commande sera imprimée automatiquement. Une imprimante ne peut viser qu'un seul Server URL à la fois.</p>
        </div>

        <div className="border-t border-white/15 pt-4">
          <div className="flex justify-between items-center mb-2">
            <Label>Codes postaux livrables</Label>
            <Button onClick={addZone} variant="outline" className="rounded-none border-white/30 bg-transparent h-8 text-xs" data-testid="add-zone-btn"><Plus className="w-3 h-3 mr-1" />Code postal</Button>
          </div>
          <div className="space-y-2">
            {s.delivery_zones.map((z, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end" data-testid={`zone-${i}`}>
                <div><span className="text-xs text-white/50">Code postal</span><Input value={z.postal_code} onChange={(e) => updZone(i, { postal_code: e.target.value })} className="rounded-none bg-neutral-800 border-white/20 h-9" /></div>
                <div><span className="text-xs text-white/50">Min (CHF)</span><Input type="number" value={z.min_order} onChange={(e) => updZone(i, { min_order: e.target.value })} className="rounded-none bg-neutral-800 border-white/20 h-9" /></div>
                <div><span className="text-xs text-white/50">Frais (CHF)</span><Input type="number" value={z.delivery_fee} onChange={(e) => updZone(i, { delivery_fee: e.target.value })} className="rounded-none bg-neutral-800 border-white/20 h-9" /></div>
                <button onClick={() => delZone(i)} className="text-red-400 h-9 flex items-center"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-white/15 pt-4">
          <Label>Export emails marketing</Label>
          <div className="mt-2">
            <Button onClick={exportEmails} variant="outline" className="rounded-none border-white/30 bg-transparent" data-testid="export-emails-btn"><Download className="w-4 h-4 mr-2" />Exporter les emails</Button>
          </div>
          {emails && <p className="text-xs text-white/50 mt-2">{emails.length} email(s)</p>}
        </div>
      </div>
    </div>
  );
}
